import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { SetStateAction, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useParams } from "react-router-dom";
import { formatDate } from "../../../helpers/formatingFunctions";
import DownloadButton from "../DownloadButton/DownloadButton";

const fetchYardRepairs = async (
  token: string | null | undefined,
  id: string | undefined,
  assetType: string
) => {
  const { data } = await axios.get(
    `${apiVariable}/getYardRepairsByUnitNumber/${id}/${assetType}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

const YardRepairs = ({ assetType }: { assetType: string }) => {
  const { token } = useAuth();
  const { id } = useParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const {
    data: assetYardRepairs,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["assetYardRepairs"],
    queryFn: () => fetchYardRepairs(token, id, assetType),
  });

  const paginatedData = assetYardRepairs
    ? assetYardRepairs.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      )
    : [];

  const handleChangePage = (event: any, newPage: SetStateAction<number>) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: { target: { value: string } }) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: "auto", mt: 4 }}>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Paper
          elevation={0}
          sx={{ p: 2, bgcolor: "error.light", color: "error.contrastText" }}
        >
          <Typography>Error: {(error as any).message}</Typography>
        </Paper>
      ) : assetYardRepairs.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ p: 3, textAlign: "center", bgcolor: "background.paper" }}
        >
          <Typography variant="body1" color="text.secondary">
            No data available.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {paginatedData.map((yardRepair: any, index: any) => (
            <Accordion
              key={index}
              sx={{
                mb: 2,
                "&:before": {
                  display: "none",
                },
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                borderRadius: "8px !important",
                overflow: "hidden",
                "&.Mui-expanded": {
                  margin: "16px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel-${yardRepair.id}-content`}
                id={`panel-${yardRepair.id}-header`}
                sx={{
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                >
                  <strong>{`Yard Repair on ${formatDate(
                    yardRepair.invoiceDate
                  )}`}</strong>
                  {`: ${yardRepair.description}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "background.default" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Mileage</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Invoice number
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Invoice date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Total Cost
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{yardRepair.mileage}</TableCell>
                        <TableCell>{yardRepair.invoiceNumber}</TableCell>
                        <TableCell>
                          {formatDate(yardRepair.invoiceDate)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {`$${yardRepair.amount}`}
                        </TableCell>
                        {yardRepair.invoiceTitle && (
                          <TableCell>
                            <DownloadButton
                              params={{
                                row: {
                                  id: yardRepair.id,
                                },
                              }}
                              type="yardRepairInvoice"
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={assetYardRepairs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-select": {
                borderRadius: 1,
              },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  margin: 0,
                },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default YardRepairs;
